import React, { useState } from "react";
import { Flex } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { useRecoilValue } from "recoil";

import { Moment } from "../Moment";
import { TMoment, TMatch } from "MVVM/Views/MatchHistory/@types";
import { MomentsViewModel } from "./ViewModel";
import { MomentsLoading } from "../../MatchesTableLoading";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { NoMoments } from "../NoMoments";
import { ESupportedGames } from "MVVM/@types";
import {
  CS2MatchHistoryFilters,
  MatchHistoryGame,
  MatchHistoryWidgetSourceState,
} from "MVVM/State/matchHistory";
import { usePortal } from "MVVM/Hooks/usePortal";
import { MatchHistoryLoginModal } from "../../LoginModal";
import { useUser } from "MVVM/Hooks/useUser";
import {
  formatGameMode,
  shortenMomentTitle,
} from "MVVM/Views/MatchHistory/utils";
import { colors } from "theme/colors";
import { OutdatedMatchInterestModal } from "MVVM/Components/Modals/OutdatedMatchInterest";

const CT_COLOR = colors.ctSide;
const T_COLOR = colors.tSide;
const BEST_COLOR = colors.bestMoment;
const ENVY_COLOR = colors.envy;
const DEFAULT_COLOR = colors.timelineDefault;

const parseMomentSide = (
  timelineLabel: string,
): { side: string; round: string } => {
  const parts = timelineLabel?.split(":") || [];
  if (parts.length === 2) {
    const round = parts[1].trim() === "999" ? "0" : parts[1].trim();
    return { side: parts[0].trim().toUpperCase(), round };
  }
  return { side: "", round: timelineLabel || "" };
};

const MomentsList = ({
  match,
  isWidget,
  outdated,
  numMomentsToRender,
}: {
  match: TMatch;
  isWidget?: boolean;
  outdated: boolean;
  numMomentsToRender?: number;
}) => {
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const cs2Filters = useRecoilValue(CS2MatchHistoryFilters);
  const limit = isWidget ? 3 : 20;
  const widgetSource = useRecoilValue(MatchHistoryWidgetSourceState);
  const { allstarUser } = useUser();
  const momentIdQueryParam = new URLSearchParams(window.location.search).get(
    "momentId",
  );
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const { moments, loading, requestOnDemandMoments } = MomentsViewModel(
    match.id,
    limit,
  );
  const momentForModal = moments.find(
    (moment: TMoment) => moment.id === momentIdQueryParam,
  );

  const matchTitle =
    !!match.title && !momentForModal ? (
      <Flex
        flexDirection="column"
        style={{ alignItems: "center", justifyContent: "center" }}
      >
        {match.title}
        <Flex style={{ fontSize: "20px", gap: "6px" }}>
          {formatGameMode(match.gameMode)}
        </Flex>
      </Flex>
    ) : null;

  const {
    portalOpen: matchHistoryLoginPortalOpen,
    setPortalOpen: setMatchHistoryLoginPortalOpen,
    Portal: MatchHistoryLoginPortal,
  } = usePortal(
    <MatchHistoryLoginModal
      game={matchHistoryGame}
      moment={momentForModal}
      matchTitle={matchTitle}
      title="matchHistoryLoginModal.title"
      defaultTitle="Don't Miss Out On This Play!"
      subtitle="matchHistoryLoginModal.subtitle"
      defaultSubtitle="Create a FREE Allstar.gg account to create, edit, and share a clip from this moment."
    />,
  );

  const bestMomentId = moments.length
    ? moments.reduce(
        (best: TMoment, m: TMoment) => (m.score > best.score ? m : best),
        moments[0],
      ).id
    : null;

  const orderedMoments = moments.length
    ? [
        moments.find((m: TMoment) => m.id === bestMomentId)!,
        ...moments
          .filter((m: TMoment) => m.id !== bestMomentId)
          .sort((a: TMoment, b: TMoment) => (a.round ?? 0) - (b.round ?? 0)),
      ]
    : [];

  const momentsToRender = numMomentsToRender
    ? orderedMoments.slice(0, numMomentsToRender)
    : orderedMoments;

  const hasSides = [ESupportedGames.CS2, ESupportedGames.VALORANT].includes(
    matchHistoryGame,
  );

  const isCs2 = matchHistoryGame === ESupportedGames.CS2;
  const hasActiveKillFilter = isCs2 && cs2Filters.minMomentKills != null;

  const getMomentKillCount = (m: TMoment) =>
    m.victimData?.length || m.victimCount || 0;

  const matchesFilter = (m: TMoment) =>
    hasActiveKillFilter && getMomentKillCount(m) >= cs2Filters.minMomentKills!;

  const bestMoment = momentsToRender.find((m) => m.id === bestMomentId);
  const bestMomentPlayerName = bestMoment?.player?.accountName || "Player";
  const bestMatchesFilter = bestMoment ? matchesFilter(bestMoment) : false;

  const nonBestMoments = momentsToRender.filter((m) => m.id !== bestMomentId);

  const filteredMoments = hasActiveKillFilter
    ? [
        ...(bestMatchesFilter && bestMoment ? [bestMoment] : []),
        ...nonBestMoments.filter((m) => matchesFilter(m)),
      ]
    : [];
  const filteredIds = new Set(filteredMoments.map((m) => m.id));
  const remainingMoments = hasActiveKillFilter
    ? nonBestMoments.filter((m) => !filteredIds.has(m.id))
    : nonBestMoments;
  const showBestSection = bestMoment && !bestMatchesFilter;

  const renderMomentRow = (
    moment: TMoment,
    prevSideRef: { current: string | null },
    isFilterSection: boolean,
  ) => {
    const { side, round } = parseMomentSide(moment.timelineLabel);
    const isCT = side === "CT";
    const sideColor = hasSides ? (isCT ? CT_COLOR : T_COLOR) : undefined;
    const isBest = moment.id === bestMomentId;
    const dotColor = isBest ? BEST_COLOR : sideColor || DEFAULT_COLOR;
    const showHalfTime =
      hasSides &&
      !isBest &&
      !isFilterSection &&
      prevSideRef.current !== null &&
      side !== prevSideRef.current;
    if (!isBest && !isFilterSection) prevSideRef.current = side;

    return (
      <React.Fragment key={moment.id}>
        {showHalfTime && (
          <HalfTimeDivider>
            <HalfTimeLine />
            <HalfTimeLabel>Side Switch</HalfTimeLabel>
            <HalfTimeLine />
          </HalfTimeDivider>
        )}
        <MomentRow
          $matchesFilter={isFilterSection}
          title={
            isFilterSection
              ? "This moment matches your filter criteria"
              : undefined
          }
        >
          <SlimTimeline
            $color={
              isBest ? BEST_COLOR : isFilterSection ? ENVY_COLOR : dotColor
            }
            $isBest={isBest}
          >
            <RoundNum>
              {hasSides ? `${side}:${round}` : moment.timelineLabel}
            </RoundNum>
            <TimelineDot>&middot;</TimelineDot>
            <TimelineTitle title={moment.title}>
              {shortenMomentTitle(moment.title)}
            </TimelineTitle>
          </SlimTimeline>
          <MomentContainer
            $widgetSource={widgetSource}
            numMomentsToRender={numMomentsToRender || 0}
          >
            <Moment
              moment={moment}
              isWidget={isWidget}
              outdated={outdated}
              matchId={match.id}
              sideColor={sideColor}
              side={hasSides ? side : undefined}
              isBest={isBest && !isFilterSection}
            />
          </MomentContainer>
        </MomentRow>
      </React.Fragment>
    );
  };

  return (
    <>
      {matchHistoryLoginPortalOpen && MatchHistoryLoginPortal}
      {showExpiredModal && (
        <OutdatedMatchInterestModal
          matchId={match.id}
          onClose={() => setShowExpiredModal(false)}
          matchPlayedAt={match.matchPlayedAt}
        />
      )}
      {loading && <MomentsLoading />}
      {!loading && !moments.length && (
        <NoMoments
          requestOnDemandMoments={
            outdated ? () => setShowExpiredModal(true) : requestOnDemandMoments
          }
        />
      )}
      {!loading &&
        !!moments.length &&
        (() => {
          const prevSideRef = { current: null as string | null };

          return (
            <>
              <ColumnHeaders>
                <HeaderSlot>{hasSides ? "Round" : ""}</HeaderSlot>
                <HeaderLabel>Moment</HeaderLabel>
              </ColumnHeaders>

              {filteredMoments.length > 0 && (
                <>
                  <FilterSectionDivider>
                    <HalfTimeLine />
                    <FilterSectionLabel>
                      Matches filter criteria
                    </FilterSectionLabel>
                    <HalfTimeLine />
                  </FilterSectionDivider>
                  {filteredMoments.map((moment) =>
                    renderMomentRow(moment, prevSideRef, true),
                  )}
                </>
              )}

              {showBestSection && (
                <>
                  <BestMomentDivider>
                    <HalfTimeLine />
                    <BestMomentLabel>
                      {bestMomentPlayerName}'s best moment
                    </BestMomentLabel>
                    <HalfTimeLine />
                  </BestMomentDivider>
                  {renderMomentRow(bestMoment, prevSideRef, false)}
                </>
              )}

              {remainingMoments.length > 0 &&
                (() => {
                  const firstSide = hasSides
                    ? parseMomentSide(remainingMoments[0].timelineLabel).side
                    : "";
                  const sideLabel = firstSide
                    ? `${firstSide}-Side Start`
                    : "All moments";
                  return (
                    <>
                      <HalfTimeDivider>
                        <HalfTimeLine />
                        <HalfTimeLabel>{sideLabel}</HalfTimeLabel>
                        <HalfTimeLine />
                      </HalfTimeDivider>
                      {remainingMoments.map((moment) =>
                        renderMomentRow(moment, prevSideRef, false),
                      )}
                    </>
                  );
                })()}
            </>
          );
        })()}
    </>
  );
};

const ColumnHeaders = styled(Flex)`
  align-items: center;
  gap: 0;
  margin-bottom: 4px;
  padding: 0 0 4px;
`;

const headerBase = css`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const HeaderSlot = styled.span`
  ${headerBase}
  box-sizing: border-box;
  flex-shrink: 0;
  padding-left: 11px;
  width: 163px;

  @media (max-width: 900px) {
    width: 110px;
  }

  @media (max-width: 640px) {
    width: 72px;
  }
`;

const HeaderLabel = styled.span`
  ${headerBase}
`;

const MomentContainer = styled(Flex)<{
  $widgetSource: string;
  numMomentsToRender: number;
}>`
  flex-grow: 1;
  gap: 0;
  min-width: 0;

  ${({ $widgetSource, numMomentsToRender }) =>
    $widgetSource === "iframe" &&
    !numMomentsToRender &&
    css`
      @media (max-height: 585px), (max-width: 650px) {
        :last-child {
          display: none;
        }
      }

      @media (max-height: 500px), (max-width: 500px) {
        :nth-last-child(2) {
          display: none;
        }
      }
    `}
`;

const MomentRow = styled(Flex)<{ $matchesFilter?: boolean }>`
  gap: 0;
  margin-bottom: 4px;
`;

const SlimTimeline = styled(Flex)<{ $color: string; $isBest?: boolean }>`
  align-items: center;
  border-left: 3px solid ${({ $color }) => $color};
  flex-shrink: 0;
  gap: 4px;
  justify-content: flex-start;
  overflow: hidden;
  padding-left: 8px;
  width: 160px;

  @media (max-width: 900px) {
    width: 110px;
  }

  @media (max-width: 640px) {
    width: 72px;
  }
`;

const TimelineDot = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  opacity: 0.4;
`;

const RoundNum = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  min-width: 38px;
  text-align: left;
  white-space: nowrap;
`;

const TimelineTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const HalfTimeDivider = styled(Flex)`
  align-items: center;
  gap: 12px;
  margin: 12px 0;
`;

const HalfTimeLine = styled.div`
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  flex-grow: 1;
  height: 1px;
`;

const HalfTimeLabel = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const FilterSectionDivider = styled(Flex)`
  align-items: center;
  gap: 12px;
  margin: 8px 0;
`;

const FilterSectionLabel = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const BestMomentDivider = styled(Flex)`
  align-items: center;
  gap: 12px;
  margin: 8px 0;
`;

const BestMomentLabel = styled.span`
  color: ${({ theme }) => theme.colors.bestMoment};
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export { MomentsList };
